import { apiFetch } from './client';

export interface BodyDims {
  shoulder: number;
  bust: number;
  waist: number;
  hip: number;
  leg_length: number;
  proportion: number;
}

export interface BodyProfile {
  virtual_user_id: string;
  user_id: string;
  display_name: string;
  body_model_id: string;
  body_thumb: string;
  gender: string;
  height_cm: number | null;
  weight_kg: number | null;
  body_shape: string;
  content: string;
  mbti: string;
  identity: string;
  energy: string;
  body_dims: BodyDims;
  updated_at?: string;
}

export type BodyProfileUpdate = Partial<{
  gender: string;
  height_cm: number | null;
  weight_kg: number | null;
  body_shape: string;
  content: string;
  mbti: string;
  identity: string;
  energy: string;
  body_dims: BodyDims;
}>;

export async function getBodyProfile(virtualUserId: string): Promise<BodyProfile> {
  return apiFetch(`/v1/virtual-users/${encodeURIComponent(virtualUserId)}/body-profile`);
}

export async function updateBodyProfile(
  virtualUserId: string,
  body: BodyProfileUpdate
): Promise<BodyProfile> {
  return apiFetch(`/v1/virtual-users/${encodeURIComponent(virtualUserId)}/body-profile`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function uploadBodyImage(virtualUserId: string, file: File): Promise<BodyProfile> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch(`/v1/virtual-users/${encodeURIComponent(virtualUserId)}/body-image`, {
    method: 'POST',
    body: form,
  });
}

export const EMPTY_DIMS: BodyDims = {
  shoulder: 0,
  bust: 0,
  waist: 0,
  hip: 0,
  leg_length: 0,
  proportion: 0,
};

export interface BodyRegionLayout {
  /** Whole-figure height. Driven only by height_cm. */
  heightScale: number;
  /** Local width multipliers. Weight adds a small shared baseline; each slider stays local. */
  shoulder: number;
  bust: number;
  waist: number;
  hip: number;
  /** Torso length vs leg length. */
  torso: number;
  leg: number;
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Regional 2D layout. Shoulder / bust / waist / hip do not share one scaleX,
 * so moving 肩宽 only changes the shoulder band.
 */
export function bodyRegionLayout(
  p: Pick<BodyProfile, 'height_cm' | 'weight_kg' | 'body_dims'>
): BodyRegionLayout {
  const h = clamp(p.height_cm ?? 170, 140, 210);
  const w = clamp(p.weight_kg ?? 60, 35, 140);
  const d = p.body_dims ?? EMPTY_DIMS;
  const mass = clamp((w - 60) / 100, -0.25, 0.4);
  const band = (slider: number, amp: number) =>
    clamp(1 + mass * 0.06 + clamp(slider, -1, 1) * amp, 0.55, 1.6);
  const leg = clamp(d.leg_length, -1, 1);
  const prop = clamp(d.proportion, -1, 1);
  return {
    heightScale: clamp(0.86 + ((h - 160) / 50) * 0.18, 0.78, 1.18),
    shoulder: band(d.shoulder, 0.42),
    bust: band(d.bust, 0.36),
    waist: band(d.waist, 0.4),
    hip: band(d.hip, 0.38),
    torso: clamp(1 + prop * 0.14 - leg * 0.08, 0.72, 1.28),
    leg: clamp(1 + leg * 0.32 + prop * 0.06, 0.62, 1.45),
  };
}
